import { FC } from 'react'
import InputItem from '../InputItem'
import { sortDropdown } from '../../shared/formFunctions'

// Shared between food (src/modules/processing-activity) and packaging
// (src/modules/packaging-pcf/processing-activities) Processing Activity edit views - verified
// byte-identical apart from two slightly shorter description strings that packaging previously
// had; both now render the fuller food copy since neither is packaging-specific in meaning.
// No behavioural differences existed between the two (unlike Resources/Waste, which were found
// to have real setFieldTouched/note-index divergence and were intentionally left unshared).
interface DataQualitySectionProps {
  formik: any
  configuration: {
    secondaryDataReferenceSourceType: any[]
    countries: any[]
    regions: any[]
  }
  process: {
    secondaryDataReferenceSourceType?: number | null
    referenceRegionIdentifier?: number | null
  }
  onFieldChange: (e: React.ChangeEvent<any>) => void
  // Material Emission Factors carry no DQS field (bug 39326, reading 36911's "no new DQS fields
  // are added"); the three Processing Activity consumers keep it.
  showCompletenessScore?: boolean
  // 39323: packaging screens describe the reference technology in material/component terms; the
  // default below is the food wording the food Processing Activity keeps.
  referenceTechnologyTypeDescription?: string
  // packaging screens describe the proxy contribution as a match-quality scale rather than the
  // food research-area percentage breakdown; the default below is the food wording.
  referenceTechnologyProxyContributionDescription?: string
}

// 39323: packaging Processing Activity and Material Emission Factor screens both use this in
// place of the food default below.
export const PACKAGING_REFERENCE_TECHNOLOGY_TYPE_DESCRIPTION =
  'Please specify the technology type (i.e. the processed material/component that is the main product of this processing activity) that was used to generate this entry. If no proxy was used this should be identical to the main product that specifies this activity. If a proxy was used, please specify this, and indicate for which key input data a proxy was used for. Please be descriptive but very concise. More information can be documented in the LCI file.'

const FOOD_REFERENCE_TECHNOLOGY_TYPE_DESCRIPTION =
  'Please specify the technology type (i.e. the processed ingreident that is the main product of this processing activity) that was used to generate this entry. If no proxy was used this should be identical to the main product that specifies this activity. If a proxy was used, please specify this, and indicate for which key input data a proxy was used for. Please be descriptive but very concise. More information can be documented in the LCI file.'

// packaging Processing Activity, Material Emission Factor and End of Life screens use this in
// place of the food default below.
export const PACKAGING_REFERENCE_TECHNOLOGY_PROXY_CONTRIBUTION_DESCRIPTION =
  'Please indicate how closely the technology/material referenced in this record matches the actual item being modelled. Select the value that applies: Direct match (no proxy used) - 0%. Roughly similar match - 50%. Very different / no meaningful match - 100%.'

const FOOD_REFERENCE_TECHNOLOGY_PROXY_CONTRIBUTION_DESCRIPTION =
  "Please indicate the sum of the respective percentage contribution for the research areas that use a proxy reference technology type to complete this processing activity. The research areas and their contributions are: 'Ingredient form and quantity' 19%, 'Product information (quantity) & economic allocation' 23%, 'Co-Products & By-products' 10%, 'Resource Source & Amount' 16%, 'Processes losses (evaporation losses)' 6.5%, 'Water usage' 6.5%, 'Waste production (water and biowaste)' 19%.\n        Please indicate the value as a percentage (e.g. 16%, instead of 0.16)."

const DataQualitySection: FC<DataQualitySectionProps> = ({
  formik,
  configuration,
  process,
  onFieldChange,
  showCompletenessScore = true,
  referenceTechnologyTypeDescription = FOOD_REFERENCE_TECHNOLOGY_TYPE_DESCRIPTION,
  referenceTechnologyProxyContributionDescription = FOOD_REFERENCE_TECHNOLOGY_PROXY_CONTRIBUTION_DESCRIPTION,
}) => (
  <>
    <h5 className={'mt-5'}>Data Quality</h5>
    <div className={'piece-content-inputs mb-5'}>
      <InputItem
        name="Secondary Data Reference Source Type"
        description="If multiple references sources where used to generate this record please indicate the overall reference source type by select the reference where the majority of the data was retrieved from, if this is not possible please select the source type with the lowest ranking. If this is not possible take the reference which was published dating back the longest. Please note “Peer-reviewed published literature” can be a journal, conference publication of book chapter."
        type="select"
        showAsterisk
        defaultValue={formik.values.secondaryDataReferenceSourceType}
        onChange={onFieldChange}
        formik={formik}
        formikName="secondaryDataReferenceSourceType"
        options={sortDropdown(
          configuration.secondaryDataReferenceSourceType,
          process.secondaryDataReferenceSourceType ?? -1,
          'description'
        )}
      />
      <InputItem
        disabled
        name="Data quality reliability score"
        description="Read only"
        type="input"
        formik={formik}
        defaultValue={formik.values.dataQualityReliabilityScore || ''}
        formikName="dataQualityReliabilityScore"
      />
      <InputItem
        name="Sampling End Date"
        description="The date in which the site in its current form ceased to exist or in which data sampling ended in YYYY. If the data was equally collected from multiple studies please select the sourcing end year from the study published the furthest in the past."
        type="input"
        onChange={onFieldChange}
        formik={formik}
        defaultValue={formik.values.referencesSamplingYear || ''}
        formikName="referencesSamplingYear"
      />
      <InputItem
        name="Reference Data Publication Year"
        description="The year in which most of the data was published. If the data was equally collected from multiple studies please select the publication year from the study published the furthest in the past. "
        type="input"
        onChange={onFieldChange}
        showAsterisk
        formik={formik}
        defaultValue={formik.values.referenceDataPublicationYear || ''}
        formikName="referenceDataPublicationYear"
      />
      <InputItem
        name="Reference Sourcing Country"
        description="Please select the Country Name identifier in which the data was collected (not published). Please select the country where most of the data was collected from. Only use publication country if no other information is available."
        type="select"
        defaultValue={
          formik.values.referenceSourcingCountry ? formik.values.referenceSourcingCountry : -1
        }
        onChange={onFieldChange}
        formik={formik}
        formikName="referenceSourcingCountry"
        options={sortDropdown(
          configuration.countries,
          formik.values.referenceSourcingCountry ?? -1,
          'countryName'
        )}
      />
      <InputItem
        name="Reference Country ISO Code"
        description="Please select the Country Name identifier in which the data was collected (not published)."
        type="select"
        defaultValue={
          formik.values.referenceSourcingCountry ? formik.values.referenceSourcingCountry : -1
        }
        onChange={onFieldChange}
        formik={formik}
        formikName="referenceSourcingCountry"
        options={sortDropdown(
          configuration.countries,
          formik.values.referenceSourcingCountry ?? -1,
          'countryISO'
        )}
      />
      <InputItem
        name="Reference Region Identifier"
        description="The region identifier in which the data was collected. Please select the region where most of the data was collected from."
        type="select"
        onChange={onFieldChange}
        formikName="referenceRegionIdentifier"
        showAsterisk
        defaultValue={formik.values.referenceRegionIdentifier}
        formik={formik}
        options={sortDropdown(
          configuration.regions,
          process.referenceRegionIdentifier || 0,
          'name'
        )}
      />
      <InputItem
        name="Reference Technology Type"
        description={referenceTechnologyTypeDescription}
        type="input"
        defaultValue={formik.values.referenceTechnologyType || ''}
        onChange={onFieldChange}
        showAsterisk
        formik={formik}
        formikName="referenceTechnologyType"
      />
      <InputItem
        name="Reference Technology Proxy Contribution"
        description={referenceTechnologyProxyContributionDescription}
        type="input"
        defaultValue={
          formik.values.referenceTechnologyProxyContribution == null
            ? ''
            : formik.values.referenceTechnologyProxyContribution.toString()
        }
        onChange={onFieldChange}
        showAsterisk
        formik={formik}
        formikName="referenceTechnologyProxyContribution"
        addonTitle="%"
      />
      {showCompletenessScore && (
        <InputItem
          name="Data Quality Completeness Score"
          description="Readonly"
          type="input"
          disabled
          defaultValue={
            formik.values.dataQualityCompletenessScore == null
              ? ''
              : formik.values.dataQualityCompletenessScore.toString()
          }
          onChange={onFieldChange}
          formik={formik}
          formikName="dataQualityCompletenessScore"
        />
      )}
    </div>
    <InputItem
      name="Data Quality Comment"
      description="Please provide any comments on assumption that were made to
        determine the criteria for data quality for this record taking
        multiple references into account."
      type="text"
      onChange={onFieldChange}
      formik={formik}
      className={'w-full'}
      defaultValue={formik.values.dataQualityComment || ''}
      formikName="dataQualityComment"
    />
  </>
)

export default DataQualitySection
